/**
 * 所有 API 端点路径统一管理，按业务域分模块。
 * 子模块通过 `import { AUTH_URLS } from './urls'` 引用。
 */

export const AUTH_URLS = {
  sendLoginCode: '/auth/sms/send',
  login: '/auth/sms-login',
  appReport: '/app/report',
  logout: '/auth/logout',
  currentUser: '/auth/user',
} as const;

export const PROJECT_CONFIG_URLS = {
  publicConfig: '/project-config',
} as const;

export const DEMO_WORKSPACE_URLS = {
  current: '/demo-workspace/current',
} as const;

export const HOT_VIDEO_URLS = {
  hotVideos: '/insight/hot-videos',
  hotVideoFavorite: (id: number) => `/insight/hot-videos/${id}/favorite`,
} as const;

export const COMPETITOR_VIDEO_URLS = {
  competitorAccountVideos: '/insight/competitor-account-videos',
  competitorAccountVideosGrouped: '/insight/competitor-account-videos/grouped',
} as const;

export const COMPETITOR_ACCOUNT_URLS = {
  competitorAccounts: '/insight/competitor-accounts',
  competitorAccountsAdd: '/insight/competitor-accounts/add',
  competitorAccountsRemove: '/insight/competitor-accounts/remove',
} as const;

export const VIDEO_ANALYSIS_URLS = {
  videosAnalysisGenerate: '/insight/videos/analysis/generate',
  videosAnalysisStatus: '/insight/videos/analysis/status',
} as const;

export const LEAD_URLS = {
  leads: '/insight/leads',
  leadsOverview: '/insight/leads/overview',
  leadsFollow: '/insight/leads/follow',
  leadsStatus: '/insight/leads/status',
  leadsCollect: '/insight/leads/collect',
  leadsSuggestions: '/insight/leads/suggestions',
} as const;

export const IM_SESSION_URLS = {
  imSessions: '/insight/im-sessions',
  imSessionMessages: '/insight/im-sessions/messages',
  imSessionsCollect: '/insight/im-sessions/collect',
  imSessionsReply: '/insight/im-sessions/reply',
  imSessionsStatus: '/insight/im-sessions/status',
  privateLeads: '/insight/private-leads',
  privateLeadsStatus: '/insight/private-leads/status',
  privateLeadsSuggestions: '/insight/private-leads/suggestions',
} as const;

export const CATEGORY_URLS = {
  insightCategories: '/insight/categories',
  insightCategoriesSearch: '/insight/categories/search',
  categoryCollectTasks: '/async-tasks',
  productCategories: '/products/categories',
  shopCategories: '/products/shop-categories',
  confirmShopCategories: '/products/shop-categories/confirm',
  userCategories: '/products/user-categories',
  confirmUserCategories: '/products/user-categories/confirm',
  categoryUserProfiles: '/insight/category-user-profiles',
} as const;

export const PRODUCT_URLS = {
  products: '/products',
  productById: (productId: string) => `/products/${productId}`,
  productDetail: '/products/detail',
  productShortTitle: (productId: string) => `/products/${productId}/short-title`,
} as const;

export const VIDEO_GENERATION_URLS = {
  generatePrompt: '/video-generation/generate-prompt',
  resolveProductSource: '/video-generation/resolve-product-source',
  adaptPrompt: '/video-generation/adapt-prompt',
  submit: '/video-generation/submit',
  uploadReferenceAsset: '/video-generation/upload-resource',
  query: '/video-generation/query',
  list: '/video-generation/list',
  publishStatusList: '/video-generation/publish-status/list',
  publishedList: '/video-generation/published/list',
  publishRecord: (videoId: string) => `/video-generation/${videoId}/publish`,
  delete: (videoId: string) => `/video-generation/${videoId}`,
  generateCreativePrompt: '/video-generation/generate-creative-prompt',
  generateCreativePromptStream: '/video-generation/generate-creative-prompt-stream',
  pollCreativePromptTask: '/video-generation/creative-prompt-tasks/poll',
  generateCharacter: '/video-generation/generate-character',
  pollCharacterTask: '/video-generation/character-tasks/poll',
  generateStoryboard: '/video-generation/generate-storyboard',
  generateStoryboardFromPrompt: '/video-generation/generate-storyboard-from-prompt',
  pollStoryboardTask: '/video-generation/storyboard-tasks/poll',
  submitShotImage: '/video-generation/generate-shot-image',
  pollShotImageTasks: '/video-generation/shot-image-tasks/poll',
  extractShots: '/video-generation/extract-shots',
  pollExtractShotsTask: '/video-generation/extract-shot-tasks/poll',
  generateDynamicDescriptions: '/video-generation/generate-dynamic-descriptions',
  pollDynamicDescTask: '/video-generation/dynamic-desc-tasks/poll',
  characters: '/video-generation/characters',
  updateCharacterBio: (characterId: string) => `/video-generation/characters/${characterId}/bio`,
  updateCharacterName: (characterId: string) => `/video-generation/characters/${characterId}/name`,
  deleteCharacter: (characterId: string) => `/video-generation/characters/${characterId}`,
  regenerateCharacter: (characterId: string) => `/video-generation/characters/${characterId}/regenerate`,
} as const;

export const VIDEO_TEMPLATE_URLS = {
  list: '/video-templates/list',
  detail: (templateId: string) => `/video-templates/${templateId}`,
  favorite: (templateId: string) => `/video-templates/${templateId}/favorite`,
  addFromVideo: '/video-templates/add-from-video',
  adminAddFromVideo: '/admin/video-templates/add-from-video',
  adminAddFromGeneratedVideo: '/admin/video-templates/add-from-generated-video',
} as const;

export const POTENTIAL_PRODUCT_URLS = {
  potentialProducts: '/insight/potential-products',
  potentialProductDetail: (id: number | string) => `/insight/potential-products/${id}`,
  potentialProductSelectionLibrary: (id: number | string) => `/insight/potential-products/${id}/selection-library`,
  potentialProductSelectionLibraryList: '/insight/potential-products/selection-library',
} as const;

export const BUYING_PRODUCT_SELECTION_URLS = {
  search: '/insight/product-testing/search',
  selfOperatedHotProductCategories: '/insight/product-testing/self-operated-hot-products/categories',
  selfOperatedHotProductSearch: '/insight/product-testing/self-operated-hot-products/search',
  detail: '/insight/product-testing/detail',
  analysis: '/insight/product-testing/analysis',
  image: '/insight/product-testing/image',
  selectionLibrary: '/insight/product-testing/selection-library',
  selectionLibraryDetail: (selectionId: number | string) => `/insight/product-testing/selection-library/${selectionId}`,
} as const;

export const DASHBOARD_URLS = {
  dashboardSummary: '/insight/dashboard/summary',
  dashboardLeadOverview: '/insight/dashboard/lead-overview',
  dashboardProductRecommendations: '/insight/dashboard/product-recommendations',
  dashboardPotentialProductRecommendations: '/insight/dashboard/potential-product-recommendations',
  dashboardHotVideoRecommendations: '/insight/dashboard/hot-video-recommendations',
} as const;

export const ECOMMERCE_ACCOUNT_URLS = {
  ecommerceAccountAuth: '/ecommerce/accounts/auth',
  ecommerceAccounts: '/ecommerce/accounts',
  ecommerceAccountSyncProducts: (ecomAccountId: string) => `/ecommerce/accounts/${ecomAccountId}/sync-products`,
  ecommerceAccountDetectSession: (ecomAccountId: string) => `/ecommerce/accounts/${ecomAccountId}/detect-session`,
  ecommerceAccountSessionStatus: (ecomAccountId: string) => `/ecommerce/accounts/${ecomAccountId}/session-status`,
  creatorOverview: '/ecommerce/accounts/creator-overview',
  shopOverview: '/ecommerce/accounts/shop-overview',
  showcaseOverview: '/ecommerce/accounts/showcase-overview',
} as const;

export const DEVICE_URLS = {
  devices: '/devices',
  deviceBindQrcode: '/device/bind/qrcode',
  deviceBindStatus: '/device/bind/status',
} as const;

export const DOUYIN_QR_LOGIN_URLS = {
  createDouyinQrSession: '/auth/douyin/qr-session',
  getDouyinSessionStatus: '/auth/douyin/session',
} as const;

export const ACCOUNT_PAYMENT_URLS = {
  plans: '/account/payments/plans',
  visiblePlans: '/account/payments/visible-plans',
  computePackages: '/account/payments/compute-packages',
  visibleComputePackages: '/account/payments/visible-compute-packages',
  entitlement: '/account/payments/entitlement',
  planTiers: '/account/payments/plan-tiers',
  paySessions: '/account/payments/pay-sessions',
  paySession: (paySessionToken: string) => `/account/payments/pay-sessions/${encodeURIComponent(paySessionToken)}`,
  orders: '/account/payments/orders',
  order: (paymentOrderNo: string) => `/account/payments/orders/${encodeURIComponent(paymentOrderNo)}`,
} as const;

export const ACCOUNT_COMPUTE_URLS = {
  balance: '/account/compute/balance',
  records: '/account/compute/records',
  chargeScenarios: '/account/compute/charge-scenarios',
} as const;

export const NOTIFICATION_URLS = {
  list: '/notifications',
  read: (id: number | string) => `/notifications/${encodeURIComponent(String(id))}/read`,
  readAll: '/notifications/read-all',
} as const;
